'use client';
import { DateObject, useLocale } from '@capsicum/common';
import { useMemo, useState } from 'react';

type DateInputProps = {
	readonly dateString: string;
	readonly onChange?: (value: string) => void;
};

type DateInputReturn = {
	dateParts: DateParts;
	handleInputChange: (key: keyof OrderMap, newValue: string) => void;
	sortedLabels: Array<string>;
};

type DateComponentKeys = 'day' | 'month' | 'year';

type DateComponents<T> = {
	[K in DateComponentKeys]?: T;
};

type OrderMap = DateComponents<number | undefined>;
type DateParts = DateComponents<string>;

const useDateInput = ({ dateString, onChange }: DateInputProps): DateInputReturn => {
	const { locale } = useLocale();
	const [wasFilledOut, setWasFilledOut] = useState(false);
	// split the date string into year, month, and day
	const initialDateParts = dateString ? dateString?.split('-') : ['', '', ''];
	const [dateParts, setDateParts] = useState<DateParts>({
		day: initialDateParts[2] || '',
		month: initialDateParts[1] || '',
		year: initialDateParts[0] || ''
	});

	// generate an order map based on the locale
	const orderMap = useMemo(() => {
		const formatter = new Intl.DateTimeFormat(locale, {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		}).formatToParts(new DateObject());

		const numericParts = formatter
			.filter(part => ['year', 'month', 'day'].includes(part.type))
			.reduce<OrderMap>(
				(acc, part, index) => ({
					...acc,
					[part.type]: index
				}),
				{}
			);

		return numericParts;
	}, [locale]);

	const sortedLabels = useMemo(() => {
		return Object.keys(orderMap).sort((a, b) => {
			const keyA = a as keyof OrderMap;
			const keyB = b as keyof OrderMap;

			return (orderMap[keyA] || 0) - (orderMap[keyB] || 0);
		});
	}, [orderMap]);

	const handleInputChange = (key: keyof OrderMap, newValue: string) => {
		const updatedParts = { ...dateParts, [key]: newValue };

		setDateParts(updatedParts);

		if (wasFilledOut || Object.values(updatedParts).every(part => part)) {
			// ensure that the month and day are padded with a 0 if they are single digits
			const formattedMonth = updatedParts.month?.padStart(2, '0');
			const formattedDay = updatedParts.day?.padStart(2, '0');

			const newDateString = `${updatedParts.year}-${formattedMonth}-${formattedDay}`;

			if (newDateString.length === 10) {
				setWasFilledOut(true);
			}
			onChange?.(newDateString);
		}
	};

	return {
		dateParts,
		handleInputChange,
		sortedLabels
	};
};

export { type DateParts, type OrderMap, useDateInput };
